/**
 * The new Grid's day board (#1766). Built to threads/new-grid/mockup.html,
 * panels 1, 2, 4, 5 and 6; a deviation from the mockup is a bug
 * (docs/bible/design-to-code-handoff.md).
 *
 * Meaning comes from color, size and position, not from reading:
 *  - card color = state (to do / tech there / finished / office review /
 *    needs a person), never trade;
 *  - card width = how long it takes, with a readable floor;
 *  - left to right = the route, at its clock time when there is room;
 *  - each tech's ring = how full the day is, the green arc = what's done.
 * Everything else is one step away: hover for the detail card, right-click
 * for actions, click to open the job.
 *
 * Only presentation is new. Drops call the shell's same `onDrop`, every menu
 * item calls the shell's same handler, and helper / tech pairing drags use
 * the same MIME types as the classic board, so the server sees nothing new.
 */
import { scopeLines } from "@/lib/scope-lines";
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type DragEvent } from "react";
import {
    CalendarOff,
    Check,
    Droplets,
    Fan,
    Flame,
    FireExtinguisher,
    MoreVertical,
    Palmtree,
    Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Mark } from "@/components/mark";
import { jobMarks, workOrderMissingMark } from "@/lib/job-marks";
import { useQuietToolbar } from "@/lib/quiet-toolbar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuLabel,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { customerWoNumber } from "@/lib/wo-number";
import {
    DEFAULT_DAILY_CAPACITY_MINUTES,
    formatJobAddress,
    jobVitals,
    type DispatchJob,
    type DispatchTech,
} from "../dispatch-types";
import {
    boardHours,
    isRepairIntentJob,
    landingForDrop,
    useDayWindow,
    workSpanFor,
    type DropLanding,
    type PositionedBlock,
} from "../day-swimlane";
import { HELPER_DRAG_MIME, TECH_DRAG_MIME, type HelperChipInfo } from "../helper-chips";
import { crewChip, crewMembers, removeCrewLabel } from "../crew";
import type { OutState } from "../out-days";
import { readLoad } from "../load-basis";
import { useHighlightedJobId } from "../highlight-job-context";
import {
    CARD_STATE_LABEL,
    cardState,
    cardTitle,
    deviceBlocks,
    deviceCountLabel,
    initials,
    isCancelled,
    isPastDue,
    jobPoints,
    jobTypeWord,
    shortName,
    techCounts,
    leftChipText,
    testsThatFit,
    type CardState,
    type JobTypeWord,
    type PulseRisk,
} from "./card-state";
import { NEW_GRID_PX_PER_HOUR, cardWidthPx, layoutLane, openRooms, visitMinutes } from "./layout";
import { PERSON_INK, STATE_CARD_CLASS, STATE_DEVICE_CLASS, STATE_SWATCH_CLASS } from "./state-style";

const LABEL_W = 200;
const PX_PER_MIN = NEW_GRID_PX_PER_HOUR / 60;
const LANE_H = 64;

export interface UnslottedJob {
    jobId: string;
    label: string;
    assignedTo: string;
    /** Job status (#1609) — only the tech's own work counts toward "left". */
    status?: string | null;
}

export interface NewDayBoardActions {
    onAdjustTime?: (jobId: string) => void;
    onReassign?: (jobId: string, techId: string) => void;
    onMoveToSpecificDate?: (jobId: string) => void;
    onMoveToTomorrow?: (jobId: string) => void;
    onPushNextWeek?: (jobId: string) => void;
    onUnschedule?: (jobId: string) => void;
    onToggleConfirm?: (jobId: string, confirm: boolean) => void;
    onAddCrew?: (jobId: string, techId: string) => void;
    /** 11/3 — take one added tech off a crew job; the lead and time stay. */
    onRemoveCrew?: (jobId: string, userId: string) => void;
    onMessageTech?: (jobId: string) => void;
    onSkip?: (jobId: string) => void;
    onCancel?: (jobId: string) => void;
    moveToTomorrowLabel?: string;
    pushNextWeekLabel?: string;
}

export interface NewDayBoardProps {
    techs: DispatchTech[];
    techJobMap: Map<string, DispatchJob[]>;
    selectedDate: string;
    isToday: boolean;
    pulseByJob: Map<string, { risk: PulseRisk; note: string }>;
    outByTech: Map<string, OutState>;
    helpersByLead: Map<string, HelperChipInfo[]>;
    /** Jobs with a time but no tech yet. */
    unassignedJobs: DispatchJob[];
    /** Jobs on a tech with no time (readiness.unslotted). */
    unslotted: UnslottedJob[];
    /** Every job the board knows, for the dragged-job lookups. */
    jobsById: Map<string, DispatchJob>;
    onDrop: (jobId: string, techId: string, start: Date, ineligibleReason?: string | null) => void;
    onJobClick: (jobId: string) => void;
    draggingJobId: string | null;
    onJobDragStart: (jobId: string) => void;
    onJobDragEnd: () => void;
    onOutClick: (userId: string) => void;
    onMarkOut: (techId: string) => void;
    draggingHelperId: string | null;
    onHelperDragStart: (helperId: string) => void;
    onHelperDragEnd: () => void;
    onHelperDrop: (helperId: string, leadId: string, x: number, y: number) => void;
    onTechDrop: (techId: string, leadId: string, x: number, y: number) => void;
    /** The ✕ on a today-only pairing badge: drop today's pairing row, so the
     *  helper goes back to their everyday lead (or a full tech gets his own
     *  lane back). Same call as the classic chip's ✕ (DELETE /api/dispatch/pairings). */
    onHelperRevert?: (helper: HelperChipInfo) => void;
    techJobTypeApprovals?: Map<string, Set<string>>;
    jobJobTypeId?: Map<string, string>;
    jobTypeLabelById?: Map<string, string>;
    techCityCerts?: Map<string, Set<string>>;
    jobCityRestriction?: Map<string, { id: string; label: string }>;
    actions: NewDayBoardActions;
    /** Shown on open-room blocks for people who can run the nudge. */
    onSuggestFit?: () => void;
    /** Views › Full day (24h): show 12 am to 12 am, same as the classic board. */
    fullDayView?: boolean;
    /** "Fit day": show only the hours that hold work, same as the classic board. */
    fitDay?: boolean;
    /** The "outside these hours" chip: switch the board to the full 24-hour day.
     *  Without it the chip is a plain label. */
    onShowAllHours?: () => void;
}

function hourLabel(h: number): string {
    if (h === 0 || h === 24) return "12 am";
    if (h === 12) return "12 pm";
    return h < 12 ? `${h} am` : `${h - 12} pm`;
}

function clock(ms: number): string {
    return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function dayStartMsFor(dateKey: string, startHour: number): number {
    const d = new Date(`${dateKey}T00:00:00`);
    d.setHours(startHour, 0, 0, 0);
    return d.getTime();
}

function TradeIcon({ code, repair }: { code: string | null | undefined; repair: boolean }) {
    const cls = "h-3 w-3 shrink-0";
    if (repair) return <Wrench className={cls} aria-label="Repair" />;
    switch (code) {
        case "fire_ext":
            return <FireExtinguisher className={cls} aria-label="Fire extinguisher" />;
        case "underground_fire":
            return <Flame className={cls} aria-label="Underground fire" />;
        case "hvac":
            return <Fan className={cls} aria-label="HVAC" />;
        case "plumbing":
            return <Wrench className={cls} aria-label="Plumbing" />;
        default:
            return <Droplets className={cls} aria-label="Backflow" />;
    }
}

/**
 * Device blocks plus the count as a number, so "how many devices" reads
 * without counting squares. A narrow card keeps one block and the number.
 */
function DeviceBlocks({
    count,
    state,
    maxBlocks = 4,
}: {
    count: number | null | undefined;
    state: CardState;
    maxBlocks?: number;
}) {
    const { blocks } = deviceBlocks(count, maxBlocks);
    const label = deviceCountLabel(count);
    if (blocks === 0 || !label) return null;
    return (
        <span className="inline-flex shrink-0 items-center gap-0.5" aria-label={label} title={label}>
            {Array.from({ length: blocks }, (_, i) => (
                <i key={i} className={cn("block h-2.5 w-[7px] rounded-[2px]", STATE_DEVICE_CLASS[state])} />
            ))}
            <span className="ml-0.5 text-[11px] font-bold tabular-nums text-foreground/80">
                {Math.floor(Number(count))}
            </span>
        </span>
    );
}

/** One plain word for the kind of job. Neutral on purpose: color is state. */
function JobTypeTag({ word }: { word: JobTypeWord | null }) {
    if (!word) return null;
    return (
        <span
            className="shrink-0 rounded border border-stone-300 bg-background/80 px-1 text-[10px] font-semibold leading-[14px] text-foreground/80 dark:border-stone-600"
            data-job-type-word={word}
        >
            {word}
        </span>
    );
}

/** The hover card — everything the classic card printed, readable. */
function JobDetail({
    job,
    state,
    risk,
}: {
    job: DispatchJob;
    state: CardState;
    risk?: { risk: PulseRisk; note: string };
}) {
    const { title, customerTag } = cardTitle(job);
    // The work, as the tech reads it first on the iPad (jobs.summary).
    const allScope = scopeLines(job.summary);
    const scope = allScope.length > 4 ? [...allScope.slice(0, 3), `+${allScope.length - 3} more`] : allScope;
    const vitals = jobVitals(job);
    const [quiet] = useQuietToolbar();
    const woMark = workOrderMissingMark(job);
    const marks = quiet ? jobMarks(job) : woMark ? [woMark] : [];
    const address = formatJobAddress({
        addressLine: job.site_address_line,
        city: job.site_city,
        region: job.site_region,
        postalCode: job.site_postal_code,
    });
    const startMs = job.scheduled_start ? Date.parse(job.scheduled_start) : NaN;
    const minutes = visitMinutes(job);
    const rows: Array<[string, string]> = [];
    if (Number.isFinite(startMs)) {
        rows.push([
            "When",
            `${clock(startMs)} – ${clock(startMs + minutes * 60_000)} · ${job.job_type_name ?? job.title}`,
        ]);
    }
    rows.push([
        "State",
        CARD_STATE_LABEL[state] +
            (risk && (risk.risk === "at_risk" || risk.risk === "overdue") ? ` · ${risk.note}` : ""),
    ]);
    if ((job.asset_count ?? 0) > 0)
        rows.push(["Devices", `${job.asset_count}${job.asset_types ? ` · ${job.asset_types.toUpperCase()}` : ""}`]);
    if (job.due_by_date)
        rows.push([
            "Due",
            `${job.due_by_date.slice(5).replace("-", "/")}${job.water_provider_name ? ` · ${job.water_provider_name}` : ""}`,
        ]);
    const nums = [...vitals.refs];
    if (vitals.acct.kind === "value") nums.push(`Acct ${vitals.acct.value}`);
    if (vitals.acct.kind === "paper") nums.push("no acct — paper");
    if (vitals.acct.kind === "missing") nums.push("acct missing");
    if (nums.length) rows.push(["Numbers", nums.join(" · ")]);
    if (vitals.phone) rows.push(["Phone", vitals.phone]);
    const pts = jobPoints(job);
    if (pts > 0) rows.push(["Worth", `${pts} pts`]);
    if ((job.crew ?? []).length > 0) rows.push(["Crew", (job.crew ?? []).map((c) => c.name ?? "Tech").join(", ")]);
    return (
        <div className="w-72 space-y-2 text-xs">
            <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-bold leading-tight">{title}</div>
                {customerTag && (
                    <span className="max-w-[45%] shrink-0 truncate rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold">
                        {customerTag}
                    </span>
                )}
            </div>
            {address && <div className="text-muted-foreground">{address}</div>}
            {scope.length > 0 && (
                <div
                    className="space-y-0.5 border-l-2 border-primary/60 pl-2 text-xs font-medium leading-snug"
                    data-testid="grid-detail-scope"
                >
                    {scope.map((l, i) => (
                        <p key={i}>{l}</p>
                    ))}
                </div>
            )}
            {marks.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="grid-detail-marks">
                    {marks.map((m) => (
                        <Mark key={m.label} kind={m.kind} label={m.label} title={m.title} />
                    ))}
                </div>
            )}
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                {rows.map(([k, v]) => (
                    <div key={k} className="contents">
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd className="m-0 tabular-nums">{v}</dd>
                    </div>
                ))}
            </dl>
            <div className="text-[11px] text-muted-foreground">Click to open · ⋮ or right-click for actions</div>
        </div>
    );
}

/** The menu parts, so the same list fills the right-click menu and the ⋮ menu. */
interface MenuParts {
    Item: typeof ContextMenuItem | typeof DropdownMenuItem;
    Label: typeof ContextMenuLabel | typeof DropdownMenuLabel;
    Separator: typeof ContextMenuSeparator | typeof DropdownMenuSeparator;
}

const CONTEXT_PARTS: MenuParts = { Item: ContextMenuItem, Label: ContextMenuLabel, Separator: ContextMenuSeparator };
const DROPDOWN_PARTS: MenuParts = {
    Item: DropdownMenuItem,
    Label: DropdownMenuLabel,
    Separator: DropdownMenuSeparator,
};

function JobMenuItems({
    job,
    techs,
    actions,
    onJobClick,
    parts,
}: {
    job: DispatchJob;
    techs: DispatchTech[];
    actions: NewDayBoardActions;
    onJobClick: (jobId: string) => void;
    parts: MenuParts;
}) {
    const { Item: ContextMenuItem, Label: ContextMenuLabel, Separator: ContextMenuSeparator } = parts;
    const crew = crewMembers(job);
    const { title } = cardTitle(job);
    const confirmed = job.dispatch_lock_state === "confirmed";
    const wo = customerWoNumber(job, { includePo: false });
    const others = techs.filter((t) => t.id !== job.assigned_to);
    const label = "text-[10px] font-medium uppercase tracking-wide text-muted-foreground";
    return (
        <>
            <ContextMenuLabel className="truncate">{title}</ContextMenuLabel>
            <ContextMenuSeparator />
            <ContextMenuItem onSelect={() => onJobClick(job.id)}>Open job</ContextMenuItem>
            {actions.onAdjustTime && (
                <ContextMenuItem onSelect={() => actions.onAdjustTime?.(job.id)}>Change time</ContextMenuItem>
            )}
            {actions.onReassign && others.length > 0 && (
                <>
                    <ContextMenuSeparator />
                    <ContextMenuLabel className={label}>Move to another tech</ContextMenuLabel>
                    <div className="max-h-40 overflow-y-auto">
                        {others.map((t) => (
                            <ContextMenuItem key={t.id} onSelect={() => actions.onReassign?.(job.id, t.id)}>
                                {t.first_name} {t.last_name}
                            </ContextMenuItem>
                        ))}
                    </div>
                </>
            )}
            <ContextMenuSeparator />
            <ContextMenuLabel className={label}>Move to another day</ContextMenuLabel>
            {actions.onMoveToTomorrow && (
                <ContextMenuItem onSelect={() => actions.onMoveToTomorrow?.(job.id)}>
                    {actions.moveToTomorrowLabel ?? "Tomorrow"}
                </ContextMenuItem>
            )}
            {actions.onPushNextWeek && (
                <ContextMenuItem onSelect={() => actions.onPushNextWeek?.(job.id)}>
                    {actions.pushNextWeekLabel ?? "Next week"}
                </ContextMenuItem>
            )}
            {actions.onMoveToSpecificDate && (
                <ContextMenuItem onSelect={() => actions.onMoveToSpecificDate?.(job.id)}>Pick a date…</ContextMenuItem>
            )}
            {actions.onUnschedule && (
                <ContextMenuItem onSelect={() => actions.onUnschedule?.(job.id)}>Send back to the tray</ContextMenuItem>
            )}
            <ContextMenuSeparator />
            {actions.onToggleConfirm && (
                <ContextMenuItem onSelect={() => actions.onToggleConfirm?.(job.id, !confirmed)}>
                    {confirmed ? "Unlock time (unconfirm booking)" : "Lock time (confirm booking)"}
                </ContextMenuItem>
            )}
            {wo && (
                <ContextMenuItem
                    onSelect={() => {
                        navigator.clipboard
                            .writeText(wo)
                            .then(() => toast.success(`Copied WO ${wo}`))
                            .catch(() => toast.error("Couldn't copy — select it on the job page instead."));
                    }}
                >
                    Copy WO number
                </ContextMenuItem>
            )}
            {actions.onMessageTech && job.assigned_to && (
                <ContextMenuItem onSelect={() => actions.onMessageTech?.(job.id)}>Message the tech</ContextMenuItem>
            )}
            {actions.onAddCrew && others.length > 0 && (
                <>
                    <ContextMenuSeparator />
                    <ContextMenuLabel className={label}>Add a second tech</ContextMenuLabel>
                    <div className="max-h-32 overflow-y-auto">
                        {others
                            .filter((t) => !(job.crew ?? []).some((c) => c.user_id === t.id))
                            .map((t) => (
                                <ContextMenuItem key={t.id} onSelect={() => actions.onAddCrew?.(job.id, t.id)}>
                                    {t.first_name} {t.last_name}
                                </ContextMenuItem>
                            ))}
                    </div>
                </>
            )}
            {actions.onRemoveCrew && crew.length > 0 && (
                <>
                    <ContextMenuSeparator />
                    {crew.map((m) => (
                        <ContextMenuItem
                            key={m.userId}
                            onSelect={() => actions.onRemoveCrew?.(job.id, m.userId)}
                            title={`${m.name} comes off this job only. ${job.assigned_to_name ?? "The lead"} keeps it, and the rest of ${m.firstName}'s day is unchanged.`}
                            data-testid="new-grid-remove-crew"
                        >
                            {removeCrewLabel(m)}
                        </ContextMenuItem>
                    ))}
                </>
            )}
            {(actions.onSkip || actions.onCancel) && <ContextMenuSeparator />}
            {actions.onSkip && (
                <ContextMenuItem
                    className="text-red-700 focus:text-red-700 dark:text-red-300"
                    onSelect={() => actions.onSkip?.(job.id)}
                >
                    Mark cannot complete
                </ContextMenuItem>
            )}
            {actions.onCancel && (
                <ContextMenuItem
                    className="text-red-700 focus:text-red-700 dark:text-red-300"
                    onSelect={() => actions.onCancel?.(job.id)}
                >
                    Cancel job
                </ContextMenuItem>
            )}
        </>
    );
}

interface CardProps {
    job: DispatchJob;
    leftPx: number;
    widthPx: number;
    selectedDate: string;
    risk?: { risk: PulseRisk; note: string };
    techs: DispatchTech[];
    actions: NewDayBoardActions;
    onJobClick: (jobId: string) => void;
    onDragStart: (jobId: string) => void;
    onDragEnd: () => void;
    /** Overrides for cards that aren't laid out by time (no time / no tech). */
    forceState?: CardState;
    subtitle?: string;
}

function JobCard({
    job,
    leftPx,
    widthPx,
    selectedDate,
    risk,
    techs,
    actions,
    onJobClick,
    onDragStart,
    onDragEnd,
    forceState,
    subtitle,
}: CardProps) {
    const highlightId = useHighlightedJobId();
    const [kebabOpen, setKebabOpen] = useState(false);
    const crew = crewChip(job);
    const state = forceState ?? cardState(job, risk?.risk);
    const { title, customerTag } = cardTitle(job);
    const late = isPastDue(job, selectedDate);
    const repair = isRepairIntentJob(job);
    const typeWord = jobTypeWord(job);
    const roomy = widthPx >= 150;
    // The Quiet Toolbar (10/10): the most urgent mark rides on the card itself
    // (the hover card lists them all), in place of the bell rows that used to
    // say "tech flagged" / "parts not in" / "booked online" somewhere else.
    const [quiet] = useQuietToolbar();
    // "No work order yet" (2026-10-21) shows with the switch off too.
    const topMark = quiet ? jobMarks(job)[0] : (workOrderMissingMark(job) ?? undefined);
    const flag =
        state === "person" ? (
            <span
                className={cn(
                    "grid h-4 w-4 place-items-center rounded-full text-[10px] font-black text-white",
                    STATE_SWATCH_CLASS.person,
                )}
                aria-label="Needs a person"
            >
                !
            </span>
        ) : late ? (
            <span
                className="grid h-4 w-4 place-items-center rounded-full bg-red-600 text-[10px] font-black text-white dark:bg-red-400 dark:text-red-950"
                aria-label="Past due date"
            >
                !
            </span>
        ) : state === "finished" ? (
            <span
                className="grid h-4 w-4 place-items-center rounded-full bg-emerald-600 text-white dark:bg-emerald-400 dark:text-emerald-950"
                aria-label="Finished"
            >
                <Check className="h-3 w-3" strokeWidth={3} />
            </span>
        ) : null;
    return (
        <ContextMenu>
            {/* The hover card stays shut while the ⋮ menu is open, so it never covers the menu. */}
            <Tooltip delayDuration={450} open={kebabOpen ? false : undefined}>
                <ContextMenuTrigger asChild>
                    <TooltipTrigger asChild>
                        <div
                            role="button"
                            tabIndex={0}
                            data-job-id={job.id}
                            data-card-state={state}
                            draggable
                            onDragStart={(e) => {
                                e.dataTransfer.setData("text/plain", job.id);
                                e.dataTransfer.effectAllowed = "move";
                                onDragStart(job.id);
                            }}
                            onDragEnd={onDragEnd}
                            onClick={() => onJobClick(job.id)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") onJobClick(job.id);
                            }}
                            className={cn(
                                "group/card absolute top-1 flex cursor-pointer flex-col justify-center gap-1 overflow-hidden rounded-xl border-[1.5px] px-2.5 py-1.5 text-foreground shadow-sm transition-transform hover:-translate-y-px hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary motion-reduce:transition-none",
                                STATE_CARD_CLASS[state],
                                highlightId === job.id && "ring-2 ring-primary ring-offset-2",
                            )}
                            style={{ left: leftPx, width: widthPx, height: LANE_H - 8 }}
                        >
                            <div className="flex min-w-0 items-center gap-1.5">
                                <span
                                    className={cn(
                                        "min-w-0 flex-1 truncate text-[13px] font-bold leading-tight",
                                        state === "finished" && "text-foreground/80",
                                    )}
                                >
                                    {title}
                                </span>
                                {flag && <span className="shrink-0">{flag}</span>}
                                <DropdownMenu open={kebabOpen} onOpenChange={setKebabOpen}>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            type="button"
                                            draggable={false}
                                            aria-label="Job actions"
                                            title="Job actions"
                                            data-testid="new-grid-card-menu"
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                            className={cn(
                                                "-mr-1.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-foreground/70 transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary data-[state=open]:bg-background data-[state=open]:opacity-100",
                                                "opacity-60 group-hover/card:opacity-100 [@media(hover:none)]:opacity-100",
                                            )}
                                        >
                                            <MoreVertical className="h-3.5 w-3.5" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="end"
                                        className="w-60"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <JobMenuItems
                                            job={job}
                                            techs={techs}
                                            actions={actions}
                                            onJobClick={onJobClick}
                                            parts={DROPDOWN_PARTS}
                                        />
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                                <TradeIcon code={job.trade_code} repair={repair} />
                                <JobTypeTag word={typeWord} />
                                <DeviceBlocks
                                    count={job.asset_count}
                                    state={state}
                                    maxBlocks={widthPx >= 170 ? 4 : 1}
                                />
                                {crew && (
                                    <span
                                        className="inline-flex min-w-0 max-w-full shrink-0 items-center gap-0.5 truncate rounded-full bg-secondary px-1.5 text-[10px] font-bold leading-4 text-foreground/80"
                                        title={crew.title}
                                        aria-label={crew.title}
                                        data-testid="new-grid-crew-chip"
                                    >
                                        👥 {crew.count}
                                        {roomy ? ` · ${crew.names}` : ""}
                                    </span>
                                )}
                                {topMark ? (
                                    <Mark
                                        kind={topMark.kind}
                                        label={roomy ? topMark.label : ""}
                                        title={topMark.label}
                                        className="min-w-0 truncate px-1.5 text-[10px]"
                                    />
                                ) : subtitle ? (
                                    <span className="truncate font-semibold">{subtitle}</span>
                                ) : crew ? null : (
                                    <>
                                        {roomy && job.site_city && <span className="truncate">{job.site_city}</span>}
                                        {customerTag && widthPx >= 200 && (
                                            <span className="max-w-[90px] shrink truncate rounded bg-background/80 px-1 text-[10px] font-semibold text-foreground/80">
                                                {customerTag}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </TooltipTrigger>
                </ContextMenuTrigger>
                <TooltipContent side="bottom" align="start" className="bg-card text-foreground shadow-lg">
                    <JobDetail job={job} state={state} risk={risk} />
                </TooltipContent>
            </Tooltip>
            <ContextMenuContent className="w-60">
                <JobMenuItems job={job} techs={techs} actions={actions} onJobClick={onJobClick} parts={CONTEXT_PARTS} />
            </ContextMenuContent>
        </ContextMenu>
    );
}

/** Plain words for the helper badge's hover. */
export function helperBadgeTitle(h: HelperChipInfo, leadFirstName: string): string {
    if (h.outLabel) return `${h.name} is out today (${h.outLabel}). Drag to another tech to move them.`;
    if (h.source === "override") {
        const back = h.defaultLeadName
            ? `Click ✕ to send them back to ${h.defaultLeadName}.`
            : `Click ✕ to undo it. If they lead their own jobs, they get their own row back.`;
        return `${h.name} is with ${leadFirstName || "this tech"} today only. ${back} Or drag them to another tech.`;
    }
    return `${h.name} rides with ${leadFirstName || "this tech"} every day. Drag them to another tech for today, or to "Work solo".`;
}

/**
 * A helper riding with this lead, as a small pill under the lead's name.
 * Solid amber = the everyday pairing. White with a dashed amber edge = today
 * only, with a ✕ inside that undoes it. Red and dashed = out today.
 */
function HelperBadge({
    helper: h,
    leadFirstName,
    onDragStart,
    onDragEnd,
    onRevert,
}: {
    helper: HelperChipInfo;
    leadFirstName: string;
    onDragStart: (helperId: string) => void;
    onDragEnd: () => void;
    onRevert?: (helper: HelperChipInfo) => void;
}) {
    const todayOnly = h.source === "override" && !h.soloToday;
    const out = Boolean(h.outLabel);
    return (
        <span
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData(HELPER_DRAG_MIME, h.id);
                e.dataTransfer.effectAllowed = "move";
                onDragStart(h.id);
            }}
            onDragEnd={onDragEnd}
            title={helperBadgeTitle(h, leadFirstName)}
            data-testid="new-grid-helper-badge"
            data-pairing={out ? "out" : todayOnly ? "today" : "default"}
            className={cn(
                "inline-flex h-5 shrink-0 cursor-grab items-center gap-px rounded-full border-[1.5px] px-1 text-[10px] font-extrabold leading-none active:cursor-grabbing",
                todayOnly
                    ? "border-dashed border-amber-600 bg-card text-amber-800 dark:border-amber-400 dark:text-amber-200"
                    : "border-transparent bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100",
                // Out today (call-in or time off): red and dashed (#2153).
                out &&
                    "border-dashed border-rose-600 bg-rose-100 text-rose-700 dark:border-rose-400 dark:bg-rose-500/25 dark:text-rose-100",
                todayOnly && onRevert && "pr-0.5",
            )}
        >
            {initials(h.name.split(" ")[0], h.name.split(" ").slice(-1)[0])}
            {todayOnly && onRevert && (
                <button
                    type="button"
                    draggable={false}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRevert(h);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-label={
                        h.defaultLeadName
                            ? `Undo: send ${h.name} back to ${h.defaultLeadName}`
                            : `Undo: take ${h.name} off ${leadFirstName || "this tech"} today`
                    }
                    title={
                        h.defaultLeadName
                            ? `Send ${h.name} back to ${h.defaultLeadName}`
                            : `Take ${h.name} off ${leadFirstName || "this tech"} today`
                    }
                    data-testid="new-grid-helper-undo"
                    className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full bg-stone-700 text-[8px] font-black leading-none text-white hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary dark:bg-stone-200 dark:text-stone-900 dark:hover:bg-red-400"
                >
                    ✕
                </button>
            )}
        </span>
    );
}

/** Ring = how full the day is; the green arc = what's already done. */
function Ring({ ratio, doneRatio, over }: { ratio: number; doneRatio: number; over: boolean }) {
    const r = 19;
    const c = 2 * Math.PI * r;
    const full = Math.min(1, Math.max(0, ratio));
    const done = Math.min(1, Math.max(0, doneRatio));
    return (
        <svg viewBox="0 0 44 44" className="absolute inset-0 h-11 w-11" aria-hidden="true">
            <circle
                cx="22"
                cy="22"
                r={r}
                fill="none"
                strokeWidth="4"
                className="stroke-stone-200 dark:stroke-stone-700"
            />
            <circle
                cx="22"
                cy="22"
                r={r}
                fill="none"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${c * full} ${c}`}
                transform="rotate(-90 22 22)"
                className={over ? "stroke-[#C86A00] dark:stroke-orange-400" : "stroke-stone-400 dark:stroke-stone-500"}
            />
            {done > 0 && (
                <circle
                    cx="22"
                    cy="22"
                    r={r}
                    fill="none"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${c * done} ${c}`}
                    transform="rotate(-90 22 22)"
                    className="stroke-emerald-600 dark:stroke-emerald-400"
                />
            )}
        </svg>
    );
}

interface LaneProps {
    tech: DispatchTech;
    jobs: DispatchJob[];
    unslotted: UnslottedJob[];
    trackWidth: number;
    dayStartMs: number;
    windowHours: number;
    nowPx: number | null;
    board: NewDayBoardProps;
    /** Show the jobs this lane counts as outside the hours; gets the earliest one's start. */
    onRevealHidden?: (startMs: number) => void;
}

function TechLane({
    tech,
    jobs,
    unslotted,
    trackWidth,
    dayStartMs,
    windowHours,
    nowPx,
    board,
    onRevealHidden,
}: LaneProps) {
    const trackRef = useRef<HTMLDivElement | null>(null);
    const [hover, setHover] = useState<DropLanding | null>(null);
    const [chipHover, setChipHover] = useState(false);
    const live = jobs.filter((j) => !isCancelled(j));
    const layout = useMemo(
        () => layoutLane(live, dayStartMs, windowHours, PX_PER_MIN),
        [live, dayStartMs, windowHours],
    );
    const hiddenCount = layout.beforeCount + layout.afterCount;
    // Where the earliest hidden job starts, so the reveal can scroll to it.
    const firstHiddenMs = useMemo(() => {
        if (hiddenCount === 0) return null;
        const dayEndMs = dayStartMs + windowHours * 3_600_000;
        let first: number | null = null;
        for (const j of live) {
            const startMs = j.scheduled_start ? Date.parse(j.scheduled_start) : NaN;
            if (!Number.isFinite(startMs)) continue;
            const endMs = startMs + visitMinutes(j) * 60_000;
            if (endMs > dayStartMs && startMs < dayEndMs) continue;
            if (first == null || startMs < first) first = startMs;
        }
        return first;
    }, [hiddenCount, live, dayStartMs, windowHours]);
    // Jobs before or after the hours shown. It sits by the tech's name, which
    // never scrolls away, and says when: "+1 after 5 pm". Clicking it shows the
    // whole day and scrolls to that job.
    const hiddenWords =
        layout.beforeCount > 0 && layout.afterCount > 0
            ? `+${hiddenCount} outside these hours`
            : layout.afterCount > 0
              ? `+${hiddenCount} after ${hourLabel(new Date(dayStartMs + windowHours * 3_600_000).getHours() || 24)}`
              : `+${hiddenCount} before ${hourLabel(new Date(dayStartMs).getHours())}`;
    const hiddenTitle = `${hiddenCount} ${hiddenCount === 1 ? "job is" : "jobs are"} outside these hours.`;
    const chipClass =
        "shrink-0 whitespace-nowrap rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold leading-4 text-amber-900 dark:bg-amber-900 dark:text-amber-100";
    const outsideHoursChip =
        hiddenCount === 0 ? null : onRevealHidden && firstHiddenMs != null ? (
            <button
                type="button"
                onClick={() => onRevealHidden(firstHiddenMs)}
                className={cn(chipClass, "hover:bg-amber-200 hover:underline dark:hover:bg-amber-800")}
                title={`${hiddenTitle} Click to show all hours, 12 am to 12 am.`}
                aria-label={`${hiddenTitle} Show all hours.`}
                data-testid="new-grid-outside-hours"
            >
                {hiddenWords} · Show
            </button>
        ) : (
            <span
                className={chipClass}
                title={`${hiddenTitle} Views, then Full day, shows them.`}
                data-testid="new-grid-outside-hours"
            >
                {hiddenWords}
            </span>
        );
    const out = board.outByTech.get(tech.id) ?? null;
    const helpers = board.helpersByLead.get(tech.id) ?? [];
    // #1609: "left" is the tech's own work — timed or not — and nothing the
    // office is holding. An untimed job counts by its own status.
    const counts = techCounts([...live, ...unslotted.map((u) => ({ status: u.status ?? "scheduled" }))]);
    const pointsBooked = live.reduce((s, j) => s + jobPoints(j), 0);
    const pointsDone = live
        .filter((j) => {
            const st = cardState(j);
            return st === "finished" || st === "office";
        })
        .reduce((s, j) => s + jobPoints(j), 0);
    const minutesBooked = layout.blocks.reduce((s, b) => s + b.minutes, 0);
    const capacityMinutes = tech.daily_capacity_minutes ?? DEFAULT_DAILY_CAPACITY_MINUTES;
    const dragged = board.draggingJobId ? board.jobsById.get(board.draggingJobId) : undefined;
    const addPts = dragged && dragged.assigned_to !== tech.id ? jobPoints(dragged) : 0;
    const addMin = dragged && dragged.assigned_to !== tech.id ? visitMinutes(dragged) : 0;
    const load = readLoad({
        bookedPoints: pointsBooked + (hover ? addPts : 0),
        pointsTarget: tech.daily_points_target ?? null,
        pointsCap: tech.daily_points_cap ?? null,
        bookedMinutes: minutesBooked + (hover ? addMin : 0),
        capacityMinutes,
    });
    const doneRatio =
        load.basis === "points" && load.target > 0
            ? pointsDone / load.target
            : capacityMinutes > 0
              ? layout.blocks
                    .filter((b) => ["finished", "office"].includes(cardState(b.job)))
                    .reduce((s, b) => s + b.minutes, 0) / capacityMinutes
              : 0;
    const over = load.ratio > 1;

    // Eligibility while something is being dragged — same rule as the classic
    // board: approvals for the job type, certs for a restricted city. A drop
    // is still allowed (an override); the lane says why in words.
    const ineligibleReason = useMemo(() => {
        if (!dragged) return null;
        const parts: string[] = [];
        const jt = board.jobJobTypeId?.get(dragged.id) ?? dragged.job_type_id ?? null;
        if (jt && board.techJobTypeApprovals && !(board.techJobTypeApprovals.get(tech.id)?.has(jt) ?? false)) {
            parts.push(`Not approved for ${board.jobTypeLabelById?.get(jt) ?? "this job type"}`);
        }
        const city = board.jobCityRestriction?.get(dragged.id);
        if (city && !(board.techCityCerts?.get(tech.id)?.has(city.id) ?? false))
            parts.push(`Not approved for ${city.label}`);
        return parts.length ? parts.join(" · ") : null;
    }, [dragged, board, tech.id]);

    const landingAt = useCallback(
        (clientX: number): DropLanding => {
            const el = trackRef.current;
            if (!el) return { minutes: 0, guidePx: 0, afterJobId: null };
            const rect = el.getBoundingClientRect();
            const px = Math.max(0, Math.min(trackWidth, clientX - rect.left));
            return landingForDrop(px, layout.blocks, PX_PER_MIN, dayStartMs);
        },
        [layout.blocks, trackWidth, dayStartMs],
    );

    const onTrackDragOver = (e: DragEvent) => {
        if (e.dataTransfer.types.includes(HELPER_DRAG_MIME) || e.dataTransfer.types.includes(TECH_DRAG_MIME)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setHover(landingAt(e.clientX));
    };
    const onTrackDrop = (e: DragEvent) => {
        e.preventDefault();
        const jobId = e.dataTransfer.getData("text/plain");
        const landing = landingAt(e.clientX);
        setHover(null);
        if (!jobId) return;
        board.onDrop(jobId, tech.id, new Date(dayStartMs + landing.minutes * 60_000), ineligibleReason);
    };

    const rooms = useMemo(
        () => (out ? [] : openRooms(layout, dayStartMs, windowHours, PX_PER_MIN)),
        [out, layout, dayStartMs, windowHours],
    );
    const lastRight = layout.contentRight;
    const ghostWidth = dragged ? cardWidthPx(visitMinutes(dragged), PX_PER_MIN) : 112;
    const hoverClock = hover ? clock(dayStartMs + hover.minutes * 60_000) : null;
    const afterTitle = hover?.afterJobId
        ? cardTitle(
              board.jobsById.get(hover.afterJobId) ?? {
                  site_name: null,
                  site_label: null,
                  customer_name: null,
                  title: "",
              },
          ).title
        : null;

    const laneTint = hover
        ? over
            ? "bg-orange-50 dark:bg-orange-950/40"
            : "bg-amber-50 dark:bg-amber-950/30"
        : dragged && ineligibleReason
          ? "bg-stone-100 dark:bg-stone-900"
          : "";

    return (
        <div
            className={cn(
                "flex border-t border-border first:border-t-0",
                laneTint,
                hover && !over && "shadow-[inset_3px_0_0_var(--color-primary)]",
            )}
            data-testid="new-grid-lane"
            data-tech-id={tech.id}
        >
            {/* Who — frozen column. Also the drop target for helper / tech
                pairing drags, exactly like the classic lane header. */}
            <ContextMenu>
                <ContextMenuTrigger asChild>
                    <div
                        className={cn(
                            "sticky left-0 z-20 flex shrink-0 items-center gap-2.5 border-r border-border bg-card px-3",
                            chipHover && "bg-sky-50 dark:bg-sky-950",
                            board.draggingHelperId &&
                                board.draggingHelperId !== tech.id &&
                                "ring-2 ring-inset ring-sky-500/70",
                        )}
                        style={{ width: LABEL_W, minHeight: LANE_H }}
                        onDragOver={(e) => {
                            if (
                                !e.dataTransfer.types.includes(HELPER_DRAG_MIME) &&
                                !e.dataTransfer.types.includes(TECH_DRAG_MIME)
                            )
                                return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            setChipHover(true);
                        }}
                        onDragLeave={() => setChipHover(false)}
                        onDrop={(e) => {
                            setChipHover(false);
                            const helperId = e.dataTransfer.getData(HELPER_DRAG_MIME);
                            if (helperId) {
                                if (helperId === tech.id) return;
                                e.preventDefault();
                                board.onHelperDrop(helperId, tech.id, e.clientX, e.clientY);
                                return;
                            }
                            const techId = e.dataTransfer.getData(TECH_DRAG_MIME);
                            if (!techId || techId === tech.id) return;
                            e.preventDefault();
                            board.onTechDrop(techId, tech.id, e.clientX, e.clientY);
                        }}
                    >
                        <div
                            className="relative h-11 w-11 shrink-0"
                            title={
                                out
                                    ? `${tech.first_name} is out — ${out.label}`
                                    : `${tech.first_name} ${tech.last_name} · ${load.booked}/${load.target} ${load.basis === "points" ? "pts" : "h"} booked`
                            }
                        >
                            {!out && <Ring ratio={load.ratio} doneRatio={doneRatio} over={over} />}
                            <div
                                className={cn(
                                    "absolute grid place-items-center rounded-full bg-secondary text-[13px] font-bold",
                                    out ? "inset-0" : "inset-[6px]",
                                )}
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData(TECH_DRAG_MIME, tech.id);
                                    e.dataTransfer.effectAllowed = "move";
                                }}
                            >
                                {initials(tech.first_name, tech.last_name)}
                            </div>
                        </div>
                        <div className="min-w-0 pl-1">
                            <div className="truncate text-sm font-bold" title={`${tech.first_name} ${tech.last_name}`}>
                                {shortName(tech.first_name, tech.last_name)}
                            </div>
                            {out ? (
                                <button
                                    type="button"
                                    onClick={() => board.onOutClick(tech.id)}
                                    className="mt-0.5 inline-flex max-w-full items-center gap-1 truncate whitespace-nowrap rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-semibold"
                                    title={
                                        out.clearable
                                            ? "Click to move or clear this out day"
                                            : "From a PTO request or call-in"
                                    }
                                >
                                    <Palmtree className="h-3 w-3" /> Out · {out.label}
                                </button>
                            ) : dragged && ineligibleReason ? (
                                <div
                                    className="truncate text-[11px] font-semibold text-muted-foreground"
                                    title={ineligibleReason}
                                >
                                    {ineligibleReason}
                                </div>
                            ) : counts.total === 0 && unslotted.length === 0 ? (
                                <div className="text-xs text-muted-foreground">Open all day</div>
                            ) : (
                                <div
                                    className="flex items-baseline gap-1 text-xs text-muted-foreground"
                                    title={leftChipText(counts).title}
                                    data-testid="new-grid-left"
                                >
                                    <b className="text-lg font-extrabold leading-none text-foreground tabular-nums">
                                        {counts.left}
                                    </b>
                                    <span>left</span>
                                    {counts.office > 0 && (
                                        <span className="font-semibold text-violet-700 dark:text-violet-300">
                                            +{counts.office} office
                                        </span>
                                    )}
                                </div>
                            )}
                            {helpers.length > 0 && (
                                <div
                                    className="mt-1 flex flex-wrap items-center gap-0.5"
                                    data-testid="new-grid-lane-helpers"
                                >
                                    {helpers.map((h) => (
                                        <HelperBadge
                                            key={h.id}
                                            helper={h}
                                            leadFirstName={tech.first_name ?? ""}
                                            onDragStart={board.onHelperDragStart}
                                            onDragEnd={board.onHelperDragEnd}
                                            onRevert={board.onHelperRevert}
                                        />
                                    ))}
                                    {!out && outsideHoursChip}
                                </div>
                            )}
                            {!out && helpers.length === 0 && (layout.blocks.length > 0 || hiddenCount > 0) && (
                                <div className="mt-1 flex items-center gap-1.5 overflow-hidden">
                                    {outsideHoursChip}
                                    <div className="flex shrink-0 gap-[3px]" aria-hidden="true">
                                        {layout.blocks.map((b) => {
                                            const st = cardState(b.job, board.pulseByJob.get(b.job.id)?.risk);
                                            return (
                                                <i
                                                    key={b.job.id}
                                                    className={cn(
                                                        "block h-2 w-2 rounded-[3px] border-[1.5px]",
                                                        st === "todo"
                                                            ? "border-stone-400 bg-transparent"
                                                            : cn("border-transparent", STATE_SWATCH_CLASS[st]),
                                                    )}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </ContextMenuTrigger>
                <ContextMenuContent className="w-56">
                    <ContextMenuLabel>
                        {tech.first_name} {tech.last_name}
                    </ContextMenuLabel>
                    <ContextMenuSeparator />
                    {out ? (
                        <ContextMenuItem onSelect={() => board.onOutClick(tech.id)}>
                            Move or clear the out day
                        </ContextMenuItem>
                    ) : (
                        <ContextMenuItem onSelect={() => board.onMarkOut(tech.id)}>
                            <CalendarOff className="mr-2 h-4 w-4" /> Mark {tech.first_name} out
                        </ContextMenuItem>
                    )}
                </ContextMenuContent>
            </ContextMenu>

            {/* The day. */}
            <div
                ref={trackRef}
                className={cn(
                    "relative shrink-0",
                    out && "bg-[repeating-linear-gradient(135deg,var(--color-secondary)_0_10px,transparent_10px_20px)]",
                )}
                style={{ width: trackWidth, height: LANE_H }}
                onDragOver={onTrackDragOver}
                onDragLeave={() => setHover(null)}
                onDrop={onTrackDrop}
                title={dragged && ineligibleReason ? ineligibleReason : undefined}
            >
                {Array.from({ length: Math.max(0, windowHours - 1) }, (_, i) => (
                    <i
                        key={i}
                        className="absolute inset-y-0 w-px bg-border/70"
                        style={{ left: (i + 1) * NEW_GRID_PX_PER_HOUR }}
                    />
                ))}
                {nowPx != null && (
                    <i className="absolute inset-y-0 z-10 w-0.5 bg-red-500/70" style={{ left: nowPx }} title="Now" />
                )}
                {out && (
                    <button
                        type="button"
                        onClick={() => board.onOutClick(tech.id)}
                        className="absolute left-3 top-1/2 z-[5] inline-flex -translate-y-1/2 items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-bold"
                        title="Right-click the name, or click here, to move or clear this out day"
                    >
                        <Palmtree className="h-3.5 w-3.5" /> Out all day · {out.label}
                    </button>
                )}
                {rooms.map((r) => (
                    <div
                        key={r.leftPx}
                        className="pointer-events-none absolute top-1 flex items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-emerald-600/70 text-xs font-semibold text-emerald-800 dark:border-emerald-400/70 dark:text-emerald-200"
                        style={{ left: r.leftPx, width: r.widthPx, height: LANE_H - 8 }}
                    >
                        {r.widthPx >= 150 && (
                            <span>
                                {live.length === 0 && r.minutes >= 6 * 60
                                    ? "Open all day"
                                    : `Room for about ${testsThatFit(r.minutes)} tests`}
                            </span>
                        )}
                        {board.onSuggestFit && r.widthPx >= 300 && (
                            <button
                                type="button"
                                className="pointer-events-auto rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-semibold text-primary-foreground"
                                onClick={board.onSuggestFit}
                            >
                                Suggest a fit
                            </button>
                        )}
                    </div>
                ))}
                {layout.gaps
                    .filter(
                        (g) => !rooms.some((r) => r.leftPx < g.leftPx + g.widthPx && g.leftPx < r.leftPx + r.widthPx),
                    )
                    .map((g) => (
                        <i
                            key={g.leftPx}
                            className="absolute top-1/2 border-t-2 border-dotted border-stone-300 dark:border-stone-600"
                            style={{ left: g.leftPx, width: g.widthPx }}
                            title={`${g.minutes} min open`}
                        />
                    ))}
                {layout.blocks.map((b: PositionedBlock) => (
                    <JobCard
                        key={b.job.id}
                        job={b.job}
                        leftPx={b.leftPx}
                        widthPx={b.widthPx}
                        selectedDate={board.selectedDate}
                        risk={board.pulseByJob.get(b.job.id)}
                        techs={board.techs}
                        actions={board.actions}
                        onJobClick={board.onJobClick}
                        onDragStart={board.onJobDragStart}
                        onDragEnd={board.onJobDragEnd}
                    />
                ))}
                {/* Assigned with no time: orange, at the end of the route —
                    a time grid can't place it, so it must not vanish. */}
                {unslotted.map((u, i) => {
                    const job =
                        board.jobsById.get(u.jobId) ??
                        ({
                            id: u.jobId,
                            title: u.label,
                            status: "scheduled",
                            customer_name: u.label,
                            assigned_to: tech.id,
                            assigned_to_name: null,
                            trade_name: null,
                            trade_code: null,
                            scheduled_start: null,
                            scheduled_end: null,
                        } satisfies DispatchJob);
                    return (
                        <JobCard
                            key={u.jobId}
                            job={job}
                            leftPx={(out ? 300 : Math.max(lastRight + 12, 0)) + i * 158}
                            widthPx={150}
                            selectedDate={board.selectedDate}
                            techs={board.techs}
                            actions={board.actions}
                            onJobClick={board.onJobClick}
                            onDragStart={board.onJobDragStart}
                            onDragEnd={board.onJobDragEnd}
                            forceState="person"
                            subtitle="No time set"
                        />
                    );
                })}
                {hover && (
                    <>
                        <div
                            className="pointer-events-none absolute top-1 z-20 rounded-xl border-2 border-dashed border-primary bg-amber-50/90 dark:bg-amber-950/80"
                            style={{ left: hover.guidePx, width: ghostWidth, height: LANE_H - 8 }}
                        />
                        <div
                            className="pointer-events-none absolute z-30 whitespace-nowrap rounded-lg bg-card px-2.5 py-1 text-xs shadow-lg ring-1 ring-border"
                            style={{ left: hover.guidePx, top: LANE_H - 6 }}
                        >
                            <b>Drop at {hoverClock}</b>
                            {afterTitle && <span className="text-muted-foreground"> · right after {afterTitle}</span>}
                            {(addPts > 0 || addMin > 0) && (
                                <span
                                    className={cn(
                                        "ml-1",
                                        over ? cn("font-semibold", PERSON_INK) : "text-muted-foreground",
                                    )}
                                >
                                    · {load.booked}/{load.target} {load.basis === "points" ? "pts" : "h"}
                                    {over ? " — over a full day" : ""}
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/** Time but no tech: their own lane at the top, every card orange. */
function NoTechLane({
    jobs,
    trackWidth,
    dayStartMs,
    windowHours,
    board,
}: Omit<LaneProps, "tech" | "unslotted" | "jobs" | "nowPx"> & { jobs: DispatchJob[] }) {
    const layout = useMemo(
        () => layoutLane(jobs, dayStartMs, windowHours, PX_PER_MIN),
        [jobs, dayStartMs, windowHours],
    );
    return (
        <div className="flex border-t border-border first:border-t-0" data-testid="new-grid-notech-lane">
            <div
                className="sticky left-0 z-20 flex shrink-0 items-center gap-2.5 border-r border-border bg-card px-3"
                style={{ width: LABEL_W, minHeight: LANE_H }}
            >
                <div
                    className={cn(
                        "grid h-11 w-11 shrink-0 place-items-center rounded-full text-lg font-black text-white",
                        STATE_SWATCH_CLASS.person,
                    )}
                >
                    ?
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-bold">No tech yet</div>
                    <div className={cn("text-xs font-semibold", PERSON_INK)}>Drag each onto a tech</div>
                </div>
            </div>
            <div className="relative shrink-0" style={{ width: trackWidth, height: LANE_H }}>
                {layout.blocks.map((b) => (
                    <JobCard
                        key={b.job.id}
                        job={b.job}
                        leftPx={b.leftPx}
                        widthPx={b.widthPx}
                        selectedDate={board.selectedDate}
                        techs={board.techs}
                        actions={board.actions}
                        onJobClick={board.onJobClick}
                        onDragStart={board.onJobDragStart}
                        onDragEnd={board.onJobDragEnd}
                        forceState="person"
                    />
                ))}
            </div>
        </div>
    );
}

export function NewDayBoard(props: NewDayBoardProps) {
    const configured = useDayWindow();
    const { fullDayView = false, fitDay = false, onShowAllHours } = props;
    const workSpan = useMemo(
        () => (fitDay ? workSpanFor(props.techs, props.techJobMap) : null),
        [fitDay, props.techs, props.techJobMap],
    );
    const hours = boardHours(configured, { fullDayView, workSpan });
    const startHour = hours.startHour;
    const windowHours = Math.max(1, hours.endHour - hours.startHour);
    const dayStartMs = useMemo(() => dayStartMsFor(props.selectedDate, startHour), [props.selectedDate, startHour]);

    // The board is as wide as the busiest lane, so every card is reachable.
    const contentRight = useMemo(() => {
        let max = 0;
        for (const t of props.techs) {
            const jobs = (props.techJobMap.get(t.id) ?? []).filter((j) => !isCancelled(j));
            const lane = layoutLane(jobs, dayStartMs, windowHours, PX_PER_MIN);
            const extra = props.unslotted.filter((u) => u.assignedTo === t.id).length * 158;
            max = Math.max(max, lane.contentRight + (extra ? extra + 12 : 0));
        }
        return max;
    }, [props.techs, props.techJobMap, props.unslotted, dayStartMs, windowHours]);
    const trackWidth = Math.max(windowHours * NEW_GRID_PX_PER_HOUR, contentRight + 24);
    const nowMs = Date.now();
    const nowPx =
        props.isToday && nowMs > dayStartMs && nowMs < dayStartMs + windowHours * 3_600_000
            ? ((nowMs - dayStartMs) / 60_000) * PX_PER_MIN
            : null;

    // Open where the work is, like the classic board (#762): the 24-hour day
    // opens at the usual start of work (or at "now" on today), not at a blank
    // midnight. After "Show" on the outside-hours chip, open at that job.
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const revealMsRef = useRef<number | null>(null);
    const scrollKey = `${props.selectedDate}|${startHour}|${windowHours}`;
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const visible = Math.max(0, el.clientWidth - LABEL_W);
        let left: number;
        if (revealMsRef.current != null) {
            left = ((revealMsRef.current - dayStartMs) / 60_000) * PX_PER_MIN - NEW_GRID_PX_PER_HOUR;
            revealMsRef.current = null;
        } else if (nowPx != null && startHour < configured.startHour) {
            left = nowPx - visible * 0.3;
        } else {
            left = (configured.startHour - startHour) * NEW_GRID_PX_PER_HOUR;
        }
        el.scrollLeft = Math.max(0, left);
        // Only when the day or its hours change — never fight the user's own scrolling.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollKey, configured.startHour]);
    const revealHidden = useCallback(
        (startMs: number) => {
            if (!onShowAllHours) return;
            revealMsRef.current = startMs;
            onShowAllHours();
        },
        [onShowAllHours],
    );
    const unslottedByTech = useMemo(() => {
        const m = new Map<string, UnslottedJob[]>();
        for (const u of props.unslotted) m.set(u.assignedTo, [...(m.get(u.assignedTo) ?? []), u]);
        return m;
    }, [props.unslotted]);

    return (
        <TooltipProvider delayDuration={450}>
            <div
                className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
                data-testid="new-grid-board"
            >
                <div className="overflow-x-auto" ref={scrollRef} data-testid="new-grid-scroll">
                    <div style={{ width: LABEL_W + trackWidth }}>
                        <div className="flex border-b border-border">
                            <div className="sticky left-0 z-20 shrink-0 bg-card" style={{ width: LABEL_W }} />
                            <div
                                className="relative h-7 shrink-0 text-[11px] font-semibold text-muted-foreground"
                                style={{ width: trackWidth }}
                            >
                                {Array.from({ length: windowHours + 1 }, (_, i) => (
                                    <span
                                        key={i}
                                        className="absolute top-1.5 -translate-x-1/2 whitespace-nowrap"
                                        style={{ left: Math.max(14, i * NEW_GRID_PX_PER_HOUR) }}
                                    >
                                        {hourLabel(startHour + i)}
                                    </span>
                                ))}
                            </div>
                        </div>
                        {props.unassignedJobs.length > 0 && (
                            <NoTechLane
                                jobs={props.unassignedJobs}
                                trackWidth={trackWidth}
                                dayStartMs={dayStartMs}
                                windowHours={windowHours}
                                board={props}
                            />
                        )}
                        {props.techs.map((t) => (
                            <TechLane
                                key={t.id}
                                tech={t}
                                jobs={props.techJobMap.get(t.id) ?? []}
                                unslotted={unslottedByTech.get(t.id) ?? []}
                                trackWidth={trackWidth}
                                dayStartMs={dayStartMs}
                                windowHours={windowHours}
                                nowPx={nowPx}
                                board={props}
                                onRevealHidden={onShowAllHours ? revealHidden : undefined}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
}
